import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { events, siteInfo } from "../data/mockData";
import { Calendar, Clock, ArrowRight, MapPin, Users, Check } from "lucide-react";
import useInView from "../hooks/useInView";

const Events = () => {
  const [eventsRef, eventsInView] = useInView();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div>
      {/* Hero */}
      <section className="relative h-[60vh] min-h-[500px] flex items-center">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              "url(/images/events.jpg)",
          }}
        >
          <div className="absolute inset-0 bg-[#1B2A4A]/75" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 w-full">
          <span className="text-[#C9A96E] text-sm font-medium tracking-[0.2em] uppercase">
            Celebrations
          </span>
          <h1 className="text-5xl md:text-6xl font-serif font-bold text-white mt-3">
            Events & Banquets
          </h1>
          <p className="text-white/70 text-lg mt-4 max-w-xl">
            From intimate gatherings to grand celebrations, our elegant spaces
            and dedicated staff make every event unforgettable.
          </p>
        </div>
      </section>

      {/* Banquet Info */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-[#C9A96E] text-sm font-medium tracking-[0.2em] uppercase">
                Our Venue
              </span>
              <h2 className="text-4xl font-serif font-bold text-[#1B2A4A] mt-3 mb-6">
                Elegant Banquet Hall
              </h2>
              <p className="text-gray-600 leading-relaxed mb-6">
                Our air-conditioned banquet hall provides a stunning backdrop
                for any occasion. With capacity for 30 to 200 seated guests,
                the space can be customized to match your vision perfectly.
              </p>
              <p className="text-gray-600 leading-relaxed mb-8">
                Surrounded by countryside views, mature trees, and manicured
                lawns, Willow Ridge creates an atmosphere that is both elegant
                and welcoming. Our experienced team handles every detail.
              </p>
              <div className="grid grid-cols-2 gap-6 mb-8">
                <div className="flex items-center gap-3">
                  <Users size={20} className="text-[#C9A96E]" />
                  <span className="text-[#1B2A4A] font-medium">30-200 Guests</span>
                </div>
                <div className="flex items-center gap-3">
                  <MapPin size={20} className="text-[#C9A96E]" />
                  <span className="text-[#1B2A4A] font-medium">Scenic Grounds</span>
                </div>
              </div>
              <h3 className="text-lg font-serif font-bold text-[#1B2A4A] mb-4">
                Perfect For:
              </h3>
              <div className="grid grid-cols-2 gap-3">
                {[
                  "Corporate Dinners",
                  "Anniversary Parties",
                  "Birthday Celebrations",
                  "Christmas Parties",
                  "Meetings & Conferences",
                  "Retirement Parties",
                  "Fundraising Events",
                  "Holiday Gatherings",
                  "Celebrations of Life",
                ].map((item) => (
                  <div key={item} className="flex items-center gap-2 text-gray-600 text-sm">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#C9A96E]" />
                    {item}
                  </div>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <img
                src="/images/events.jpg"
                alt="Event Space"
                className="rounded-xl h-64 object-cover w-full"
              />
              <img
                src="/images/dining2.jpg"
                alt="Dining Setup"
                className="rounded-xl h-64 object-cover w-full mt-8"
              />
              <img
                src="/images/golf_hero.jpg"
                alt="Venue View"
                className="rounded-xl h-64 object-cover w-full -mt-8"
              />
              <img
                src="/images/img1875.jpeg"
                alt="Grounds"
                className="rounded-xl h-64 object-cover w-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Cocktail Coach */}
      <section ref={eventsRef} className="py-24 bg-[#f8f7f4]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">

          {/* Header */}
          <div className="text-center mb-14">
            <span className="text-[#C9A96E] text-sm font-medium tracking-[0.2em] uppercase">Mobile Bar Service</span>
            <h2 className="text-4xl font-serif font-bold text-[#1B2A4A] mt-3 mb-4">Cocktail Coach</h2>
            <p className="text-gray-500 max-w-xl mx-auto text-base">Your Event, Our Bar — Infinite Memories. Bringing the perfect bar experience to your special occasion.</p>
            <div className="flex flex-wrap justify-center gap-3 mt-5">
              <span className="inline-flex items-center gap-1.5 bg-white border border-gray-200 text-[#1B2A4A] text-xs font-semibold px-4 py-2 rounded-full shadow-sm">
                <Check size={12} className="text-[#C9A96E]" /> Fully Licensed &amp; Insured
              </span>
              <span className="inline-flex items-center gap-1.5 bg-white border border-gray-200 text-[#1B2A4A] text-xs font-semibold px-4 py-2 rounded-full shadow-sm">
                <Check size={12} className="text-[#C9A96E]" /> Smart Serve Certified
              </span>
              <span className="inline-flex items-center gap-1.5 bg-white border border-gray-200 text-[#1B2A4A] text-xs font-semibold px-4 py-2 rounded-full shadow-sm">
                <Check size={12} className="text-[#C9A96E]" /> Chatham, Blenheim &amp; Surrounding Areas
              </span>
            </div>
          </div>

          {/* Image + Intro */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-20">
            <div className="overflow-hidden rounded-2xl shadow-lg">
              <img
                src="/images/59fupkgu_img1362.jpeg"
                alt="Cocktail Coach mobile bar"
                className="w-full h-[420px] object-cover hover:scale-105 transition-transform duration-700"
              />
            </div>
            <div>
              <span className="text-[#C9A96E] text-sm font-medium tracking-[0.2em] uppercase">By Willow Ridge</span>
              <h3 className="text-3xl font-serif font-bold text-[#1B2A4A] mt-3 mb-5">Premium Mobile Bar for Every Occasion</h3>
              <p className="text-gray-600 leading-relaxed mb-6">
                As part of Willow Ridge Golf & Country Club, Cocktail Coach brings years of hospitality excellence to your special event. Our mobile bar service combines professional bartending with the flexibility to serve anywhere in Southern Ontario.
              </p>
              <div className="grid grid-cols-2 gap-4 mb-8">
                {[
                  { label: "Custom Experiences", desc: "Signature cocktails & themed bars" },
                  { label: "Setup & Cleanup", desc: "We handle everything" },
                  { label: "6 Hours Included", desc: "In all packages" },
                  { label: "Booking for 2026", desc: "Weddings, corporate & more" },
                ].map((item) => (
                  <div key={item.label} className="bg-white rounded-xl p-4 shadow-sm">
                    <div className="text-sm font-semibold text-[#1B2A4A] mb-0.5">{item.label}</div>
                    <div className="text-xs text-gray-500">{item.desc}</div>
                  </div>
                ))}
              </div>
              <a
                href="https://cocktailcoach.ca"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-8 py-4 bg-[#1B2A4A] text-white font-semibold rounded-md hover:bg-[#2a3d62] transition-colors duration-300"
              >
                Book Your Bar Experience <ArrowRight size={18} />
              </a>
            </div>
          </div>

          {/* Packages */}
          <div className="text-center mb-10">
            <span className="text-[#C9A96E] text-sm font-medium tracking-[0.2em] uppercase">Pricing</span>
            <h3 className="text-3xl font-serif font-bold text-[#1B2A4A] mt-2">Choose Your Bar Experience</h3>
          </div>
          <div className={`grid grid-cols-1 md:grid-cols-3 gap-6 ${eventsInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"} transition-all duration-700`}>
            {/* Essentials */}
            <div className="bg-white rounded-2xl p-7 shadow-sm flex flex-col">
              <div className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-2">The Essentials Bar</div>
              <div className="text-3xl font-bold text-[#1B2A4A] mb-0.5">$750 <span className="text-sm font-normal text-gray-400">flat fee</span></div>
              <p className="text-sm text-gray-500 mb-5">Simple. Stylish. Stress-free. Perfect for small parties, birthdays, or backyard gatherings.</p>
              <ul className="space-y-2 text-sm text-gray-600 flex-1">
                {[
                  "Fully Licensed & Insured",
                  "1 Professional Bartender",
                  "Up to 75 guests | 6 hours",
                  "Mobile Bar Setup",
                  "Ice, Garnishes & Mixers",
                  "Domestic Beer, Wine & Mixed Drinks",
                  "Host, cash or ticket bar options",
                ].map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <Check size={13} className="text-[#C9A96E] mt-0.5 shrink-0" /> {f}
                  </li>
                ))}
              </ul>
              <a href="https://cocktailcoach.ca" target="_blank" rel="noopener noreferrer" className="mt-6 block text-center py-3 border-2 border-[#1B2A4A] text-[#1B2A4A] text-sm font-semibold rounded-lg hover:bg-[#1B2A4A] hover:text-white transition-all duration-300">
                Select Package
              </a>
            </div>
            {/* Signature — featured */}
            <div className="bg-[#1B2A4A] rounded-2xl p-7 shadow-lg flex flex-col relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#C9A96E] text-[#1B2A4A] text-xs font-bold px-4 py-1 rounded-full uppercase tracking-wide">Most Popular</div>
              <div className="text-xs font-bold text-[#C9A96E] uppercase tracking-wide mb-2">The Signature Bar</div>
              <div className="text-3xl font-bold text-white mb-0.5">$950 <span className="text-sm font-normal text-white/50">flat fee</span></div>
              <p className="text-sm text-white/60 mb-5">Designed for weddings, celebrations & corporate events.</p>
              <ul className="space-y-2 text-sm text-white/80 flex-1">
                {[
                  "Everything in Essentials, PLUS:",
                  "2 Professional Bartenders",
                  "Up to 150 guests | 6 hours",
                  "Premium Bar Setup with themed décor",
                  "Premium Mixers & Disposables",
                  "2 Signature Cocktails of your choice",
                  "Personalized drink menu display",
                ].map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <Check size={13} className="text-[#C9A96E] mt-0.5 shrink-0" /> {f}
                  </li>
                ))}
              </ul>
              <a href="https://cocktailcoach.ca" target="_blank" rel="noopener noreferrer" className="mt-6 block text-center py-3 bg-[#C9A96E] text-[#1B2A4A] text-sm font-semibold rounded-lg hover:bg-[#d4b97e] transition-all duration-300">
                Select Package
              </a>
            </div>
            {/* Luxe */}
            <div className="bg-white rounded-2xl p-7 shadow-sm flex flex-col">
              <div className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-2">The Luxe Bar</div>
              <div className="text-3xl font-bold text-[#1B2A4A] mb-0.5">$1,250 <span className="text-sm font-normal text-gray-400">flat fee</span></div>
              <p className="text-sm text-gray-500 mb-5">Where presentation meets perfection. For upscale events where every detail shines.</p>
              <ul className="space-y-2 text-sm text-gray-600 flex-1">
                {[
                  "Everything in Signature, PLUS:",
                  "3 Professional Bartenders",
                  "Up to 200 guests | 6 hours",
                  "Themed Chalkboard Menu",
                  "Lighting & Outdoor Bar Seating",
                  "Mocktail & Non-Alcoholic Station",
                  "Pre-Event Consultation & Menu Design",
                ].map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <Check size={13} className="text-[#C9A96E] mt-0.5 shrink-0" /> {f}
                  </li>
                ))}
              </ul>
              <a href="https://cocktailcoach.ca" target="_blank" rel="noopener noreferrer" className="mt-6 block text-center py-3 border-2 border-[#1B2A4A] text-[#1B2A4A] text-sm font-semibold rounded-lg hover:bg-[#1B2A4A] hover:text-white transition-all duration-300">
                Select Package
              </a>
            </div>
          </div>

          <p className="text-center text-sm text-gray-400 mt-6">Need something unique? <a href="https://cocktailcoach.ca" target="_blank" rel="noopener noreferrer" className="text-[#C9A96E] hover:underline">Request a custom quote</a></p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#1B2A4A]">
        <div className="max-w-3xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-white mb-4">
            Plan Your Event With Us
          </h2>
          <p className="text-white/60 mb-8">
            Contact Shannon Thyssen, Food & Beverage Manager, to discuss
            your event details and custom packages.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#C9A96E] text-[#1B2A4A] font-semibold rounded-md hover:bg-[#d4b97e] transition-colors duration-300"
            >
              Get in Touch <ArrowRight size={18} />
            </Link>
            <a
              href={`tel:${siteInfo.phone}`}
              className="inline-flex items-center gap-2 px-8 py-4 border-2 border-white/30 text-white font-semibold rounded-md hover:bg-white/10 transition-colors duration-300"
            >
              Call (519) 676-8772 ext. 4
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Events;
